import * as React from 'react';
import { Image } from '@/hen-ui/atomics/Image';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import { Link } from '@/hen-ui/atomics/Link';
import { Card } from '@/hen-ui/composites/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
import {
  expertBioContainerVariants,
  expertBioImageVariants,
  expertBioNameVariants,
  type ExpertBioContainerVariantProps,
} from './expert-bio.variants';

export type ExpertBioHighlight = {
  label: string;
  text: string;
};

/**
 * Opinionated document-outline role for the expert name — not a free-form tag override.
 * - `default`: card/list item title (h5)
 * - `section`: primary item under a page-level heading (h2, h5 visual style)
 */
export type ExpertBioHeadingContext = 'default' | 'section';

const EXPERT_BIO_NAME_HEADING_TAG: Record<
  ExpertBioHeadingContext,
  'h5' | 'h2'
> = {
  default: 'h5',
  section: 'h2',
};

export interface ExpertBioProps extends ExpertBioContainerVariantProps {
  /**
   * Expert's profile image. When omitted, an initials placeholder is shown.
   */
  imgSrc?: string;

  imgAlt?: string;
  /**
   * Expert's name
   */
  name: string;
  /**
   * Expert's occupation
   */
  occupation?: string;
  /**
   * Expert's brand link
   */
  brandLink?: {
    href: string;
    label: string;
  };
  /**
   * Highlight snippet from CMS (e.g. Expertise or Education) with display label
   */
  highlight?: ExpertBioHighlight;
  /**
   * Document-outline role for the expert name (visual style stays h5).
   * Prefer setting via `ExpertRow` `headingContext` for list/section layouts.
   * @default 'default'
   */
  headingContext?: ExpertBioHeadingContext;
}

function formatHighlightLine(highlight: ExpertBioHighlight): string {
  const trimmedLabel = highlight.label.trim();
  if (!trimmedLabel) return highlight.text;
  return `${trimmedLabel}: ${highlight.text}`;
}

export const ExpertBio: React.FC<ExpertBioProps> = ({
  size = 'sm',
  border = 'none',
  imgSrc,
  imgAlt,
  name,
  occupation,
  brandLink,
  highlight,
  headingContext = 'default',
}) => {
  const nameClassName = expertBioNameVariants({ border });
  const NameTag = EXPERT_BIO_NAME_HEADING_TAG[headingContext];
  return (
    <Card
      variant={
        border === 'none' ? 'background-transparent' : 'background-white'
      }
      className={cn(expertBioContainerVariants({ size, border }))}
      data-hen-id={COMPONENT_IDS.ExpertBio}
    >
      {imgSrc ? (
        <Image
          src={imgSrc}
          alt={imgAlt ?? name}
          rounded={border === 'none' && size === 'lg' ? 'xl' : 'lg'}
          className={cn(expertBioImageVariants({ size }))}
        />
      ) : (
        <Flex
          direction='row'
          align='center'
          justify='center'
          className={cn(
            'bg-neutral-100',
            expertBioImageVariants({ size }),
            size === 'lg' ? 'rounded-lg' : 'rounded-md',
          )}
          aria-hidden
        >
          <Text variant='d3' className='font-semibold text-neutral-500'>
            {name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)}
          </Text>
        </Flex>
      )}

      <Flex
        direction='column'
        gap='xs'
        className={cn('min-w-0 flex-1', size === 'sm' && 'overflow-hidden')}
      >
        <Flex direction='column' gap='xxs'>
          <Heading variant='h5' asChild className={nameClassName}>
            <NameTag>{name}</NameTag>
          </Heading>
          {occupation && (
            <Text variant='b5' color='subtext'>
              {occupation}
            </Text>
          )}
        </Flex>
        {brandLink && (
          <Link
            variant='black'
            size='sm'
            href={brandLink.href}
            className='font-bold'
          >
            {brandLink.label}
          </Link>
        )}
        {highlight?.text && (
          <Text
            variant='b5'
            color='subtext'
            className={cn(size === 'sm' && 'line-clamp-2')}
          >
            {formatHighlightLine(highlight)}
          </Text>
        )}
      </Flex>
    </Card>
  );
};
